/**
 * Plan wording and 402 handling (spec §1.3.2, §1.3.3, §3.2).
 *
 * Pure: no React, no network. The banners and the refusal messages both read
 * from here so one refusal never gets two different explanations.
 */

/** §3.2 — hours mean more to people as walkthroughs than as a decimal. */
export const MINUTES_PER_WALKTHROUGH = 5;

export function walkthroughsFor(hours) {
  return Math.round(((Number(hours) || 0) * 60) / MINUTES_PER_WALKTHROUGH);
}

/** "2.5 of 20 hours · ≈ 30 five-minute walkthroughs left" */
export function describeHours(used, limit) {
  const u = Number(used) || 0;
  if (limit == null) {
    return { headline: `${round(u)} hours used`, detail: 'No limit on your own API key.' };
  }
  const left = Math.max(0, limit - u);
  return {
    headline: `${round(u)} of ${round(limit)} hours used`,
    detail: `≈ ${walkthroughsFor(left)} five-minute walkthroughs left`,
  };
}

function round(n) {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/** Days remaining on a trial; null when there isn't one. */
export function trialDaysLeft(trialEndsAt, now = Date.now()) {
  if (!trialEndsAt) return null;
  const end = parseServerTime(trialEndsAt);
  if (end == null) return null;
  return Math.max(0, Math.ceil((end - now) / 86400000));
}

/**
 * The backend stores UTC-naive timestamps ("2026-10-09 00:00:00").
 * Date.parse reads a bare ISO datetime as *local* time, which shifts the
 * trial countdown by a day for anyone west of UTC. Treat a timestamp with no
 * offset as UTC, which is what it is.
 */
export function parseServerTime(ts) {
  if (!ts) return null;
  const raw = String(ts).trim().replace(' ', 'T');
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(raw);
  const v = Date.parse(hasZone ? raw : `${raw}Z`);
  return Number.isNaN(v) ? null : v;
}

export const CONTACT = 'hello@affixly.ai';

/** The upgrade link every refusal and banner uses. There is no checkout yet. */
export function contactHref({ plan, tenantId, reason }) {
  const subject = encodeURIComponent(`Flow plan — ${reason || 'upgrade'}`);
  const body = encodeURIComponent(
    `Product: Flow\nAccount: ${tenantId ?? 'unknown'}\nPlan: ${plan || 'unknown'}\n\n`
  );
  return `mailto:${CONTACT}?subject=${subject}&body=${body}`;
}

// ── Banners (§1.3.3) ────────────────────────────────────────────────────────

/**
 * What to show at the top of the app, or null for nothing.
 * read-only outranks blocked, which outranks the 80% warning.
 */
export function planBanner(plan) {
  if (!plan || plan.billing_exempt) return null;
  // With PLANS_ENFORCED off nothing is refused, so nothing may claim it is.
  // status can still read "read_only" once a trial lapses (Settings shows it);
  // a banner saying work is paused while every route still works is false.
  if (plan.enforced === false) return null;

  if (plan.status === 'read_only' || plan.status === 'suspended') {
    return {
      tone: 'error',
      title: plan.status === 'suspended' ? 'This account is suspended' : 'Your trial has ended',
      body: 'Your documents stay available and anything already published stays live. '
          + 'New recordings and AI actions are paused until you upgrade.',
      action: 'Contact us to upgrade',
      reason: 'read_only',
    };
  }

  if (plan.blocked) {
    return {
      tone: 'error',
      title: "You've used everything on your plan for this period",
      body: 'Existing documents are unaffected. New processing resumes next period, '
          + 'or straight away on a larger plan.',
      action: 'Contact us to upgrade',
      reason: 'quota_exceeded',
    };
  }

  if (plan.warn) {
    const hours = (plan.usage || []).find(u => u.unit === 'hours');
    const d = hours ? describeHours(hours.used, hours.limit) : null;
    return {
      tone: 'warning',
      title: "You're close to your plan limit",
      body: d ? `${d.headline} — ${d.detail}.` : 'You are near your limit for this period.',
      action: 'Contact us to upgrade',
      reason: 'warn_80',
    };
  }
  return null;
}

// ── 402 refusals (§1.3.2) ───────────────────────────────────────────────────

const REFUSAL_CODES = new Set(['quota_exceeded', 'spend_ceiling', 'read_only', 'byok_required']);

/** Extract a plan refusal from an axios error, or null if it isn't one. */
export function planRefusal(err) {
  const res = err?.response;
  if (!res || res.status !== 402) return null;
  const d = res.data || {};
  if (!REFUSAL_CODES.has(d.code)) return null;
  return {
    code: d.code,
    unit: d.unit ?? null,
    used: d.used ?? null,
    limit: d.limit ?? null,
    message: d.message || refusalFallback(d.code),
    retryable: Boolean(d.retryable),
    action: 'Contact us to upgrade',
  };
}

function refusalFallback(code) {
  switch (code) {
    case 'read_only':
      return 'Your trial has ended. Existing documents stay available; new processing is paused.';
    case 'spend_ceiling':
      return 'This account has reached its spend limit for the current period.';
    case 'byok_required':
      return 'This plan needs your own API key for that.';
    default:
      return 'You have used all the processing on your plan for this period.';
  }
}

/** True when the refusal means no work started — so inputs are untouched. */
export function nothingStarted(refusal) {
  return Boolean(refusal) && refusal.retryable === false;
}
